/* eslint-disable react-refresh/only-export-components -- Toast helpers and re-exports co-located with Toaster component */
import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";
import { CheckCircle2, Loader2, XCircle, Info, AlertTriangle } from "lucide-react";

type ToasterProps = React.ComponentProps<typeof Sonner>;

export type LoadingToastId = string | number;

export function showLoadingToast(message: string): LoadingToastId {
  return toast.loading(message);
}

export function finishLoadingToastSuccess(id: LoadingToastId, message: string): void {
  toast.success(message, { id });
}

export function finishLoadingToastError(id: LoadingToastId, message: string, action?: ToastAction): void {
  toast.error(message, action ? { id, action } : { id });
}

export type ToastAction = { label: string; onClick: () => void };

export type ActionToastResult = {
  ok: boolean;
  message: string;
  /** A failure toast's button, e.g. "Open #51-1" when the item is on a live part. */
  action?: ToastAction;
};

/** Show a loading toast, run an async action, then resolve to success or error on the same toast. */
export async function withActionToast<T extends ActionToastResult>(
  loadingMessage: string,
  action: () => Promise<T>,
): Promise<T> {
  const id = showLoadingToast(loadingMessage);
  const result = await action();
  if (result.ok) {
    finishLoadingToastSuccess(id, result.message);
  } else {
    finishLoadingToastError(id, result.message, result.action);
  }
  return result;
}

/**
 * App-wide toast host (sonner only).
 * Position is locked to top-right — do not override per-call.
 */
const Toaster = ({ position: _ignoredPosition, ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group z-[9999]"
      icons={{
        loading: <Loader2 className="h-5 w-5 animate-spin" aria-hidden />,
        success: <CheckCircle2 className="h-5 w-5" />,
        error: <XCircle className="h-5 w-5" />,
        info: <Info className="h-5 w-5" />,
        warning: <AlertTriangle className="h-5 w-5" />,
      }}
      duration={5000}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description:
            "group-[.toast]:text-muted-foreground group-[.toast.toast-loading]:text-muted-foreground group-[.toast.toast-success]:text-success-foreground/90 group-[.toast.toast-error]:text-destructive-foreground/90 group-[.toast.toast-info]:text-info-foreground/90 group-[.toast.toast-warning]:text-warning-foreground/90",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          success: "toast-success !bg-success !text-success-foreground !border-success",
          error: "toast-error !bg-destructive !text-destructive-foreground !border-destructive",
          info: "toast-info !bg-info !text-info-foreground !border-info",
          warning: "toast-warning !bg-warning !text-warning-foreground !border-warning",
          loading: "toast-loading !bg-info !text-info-foreground !border-info",
        },
      }}
      {...props}
      position="top-right"
    />
  );
};

export { Toaster };
export { toast };
